import { clsx } from 'clsx';
import React from 'react';

import { Input } from '@design-system/components/atoms/Input';
import type { InputProps } from '@design-system/components/atoms/Input';
import { Label } from '@design-system/components/atoms/Label';

export interface FormFieldProps extends Omit<InputProps, 'error' | 'helperText'> {
  /**
   * Field label
   */
  label: string;

  /**
   * Error message (if any)
   */
  error?: string;

  /**
   * Whether field has been touched
   */
  touched?: boolean;

  /**
   * Whether field is required
   * @default false
   */
  required?: boolean;
}

/**
 * FormField molecule - Combines Label, Input, and error message
 * Designed for use with Formik
 */
export const FormField = React.forwardRef<HTMLInputElement, FormFieldProps>(
  (
    { label, error, touched, required = false, name, className, fullWidth = false, ...props },
    ref
  ) => {
    const showError = touched && error;

    return (
      <div className={clsx('flex flex-col', fullWidth && 'w-full', className)}>
        <Label htmlFor={name} required={required} error={Boolean(showError)}>
          {label}
        </Label>
        <Input
          ref={ref}
          id={name}
          name={name}
          error={Boolean(showError)}
          helperText={showError ? error : undefined}
          fullWidth={fullWidth}
          aria-required={required}
          {...props}
        />
      </div>
    );
  }
);

FormField.displayName = 'FormField';
